import React, { useEffect, useMemo, useState } from "react";
import axios from "axios";
import { toast } from "react-toastify";
import { useSelector } from "react-redux";

import Modal from "../../../components/Reusable/Modal";
import RichTextEditor from "../../../components/Reusable/RichTextEditor";
import { RootState } from "../../../redux/store";

interface FaqItem {
  id: number;
  question: string;
  answer: string;
}

interface ProviderFaqManagementModalProps {
  isOpen: boolean;
  onClose: () => void;
  providerId: number | null;
  providerName?: string;
  providerEndpoint: string;
}

const ProviderFaqManagementModal: React.FC<ProviderFaqManagementModalProps> = ({
  isOpen,
  onClose,
  providerId,
  providerName,
  providerEndpoint,
}) => {
  const token = useSelector((state: RootState) => state.auth.token);
  const API_URL = process.env.REACT_APP_API_URL;

  const [faqs, setFaqs] = useState<FaqItem[]>([]);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [deletingId, setDeletingId] = useState<number | null>(null);

  const [question, setQuestion] = useState("");
  const [answer, setAnswer] = useState("");
  const [editingFaq, setEditingFaq] = useState<FaqItem | null>(null);

  const title = useMemo(() => {
    if (providerName) return `Manage FAQs: ${providerName}`;
    return "Manage FAQs";
  }, [providerName]);

  const hasVisibleAnswerText = (html: string): boolean => {
    const stripped = html
      .replace(/<[^>]*>/g, "")
      .replace(/&nbsp;/g, " ")
      .trim();
    return stripped.length > 0;
  };

  const resetForm = () => {
    setQuestion("");
    setAnswer("");
    setEditingFaq(null);
  };

  const fetchFaqs = async () => {
    if (!token || !providerId || !API_URL) return;

    setLoading(true);
    try {
      const response = await axios.get(
        `${API_URL}/${providerEndpoint}/${providerId}/faqs`,
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        },
      );
      setFaqs(response.data?.data || []);
    } catch (error: any) {
      toast.error(error?.response?.data?.message || "Failed to fetch FAQs");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (!isOpen) return;
    if (!token) {
      toast.error("Unauthorized");
      return;
    }
    if (!providerId) return;

    fetchFaqs();
    resetForm();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, providerId, token, providerEndpoint]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!token || !providerId || !API_URL) return;
    if (!question.trim() || !hasVisibleAnswerText(answer)) {
      toast.error("Question and answer are required");
      return;
    }

    setSaving(true);
    try {
      if (editingFaq) {
        await axios.put(
          `${API_URL}/${providerEndpoint}/${providerId}/faqs/${editingFaq.id}`,
          { question, answer },
          {
            headers: {
              Authorization: `Bearer ${token}`,
            },
          },
        );
        toast.success("FAQ updated successfully");
      } else {
        await axios.post(
          `${API_URL}/${providerEndpoint}/${providerId}/faqs`,
          { question, answer },
          {
            headers: {
              Authorization: `Bearer ${token}`,
            },
          },
        );
        toast.success("FAQ created successfully");
      }

      resetForm();
      await fetchFaqs();
    } catch (error: any) {
      toast.error(error?.response?.data?.message || "Failed to save FAQ");
    } finally {
      setSaving(false);
    }
  };

  const handleEdit = (faq: FaqItem) => {
    setEditingFaq(faq);
    setQuestion(faq.question);
    setAnswer(faq.answer);
  };

  const handleDelete = async (faqId: number) => {
    if (!token || !providerId || !API_URL) return;

    setDeletingId(faqId);
    try {
      await axios.delete(
        `${API_URL}/${providerEndpoint}/${providerId}/faqs/${faqId}`,
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        },
      );
      toast.success("FAQ deleted successfully");
      await fetchFaqs();
      if (editingFaq?.id === faqId) {
        resetForm();
      }
    } catch (error: any) {
      toast.error(error?.response?.data?.message || "Failed to delete FAQ");
    } finally {
      setDeletingId(null);
    }
  };

  return (
    <Modal isOpen={isOpen} onClose={onClose} className="w-full max-w-2xl">
      <h2 className="text-xl font-bold mb-4">{title}</h2>

      <form onSubmit={handleSubmit} className="mb-6">
        <div className="mb-4">
          <label className="block mb-2">Question</label>
          <input
            type="text"
            value={question}
            onChange={(e) => setQuestion(e.target.value)}
            required
            className="w-full p-2 border rounded dark:bg-white/10 dark:text-white"
          />
        </div>

        <div className="mb-4">
          <label className="block mb-2">Answer</label>
          <RichTextEditor
            value={answer}
            onChange={setAnswer}
            placeholder="Write answer..."
          />
        </div>

        <div className="flex items-center gap-2">
          <button
            type="submit"
            disabled={saving}
            className="px-4 py-2 bg-gradient-to-r from-[#027EC2] to-[#039EF2] text-white rounded-lg disabled:opacity-70"
          >
            {saving ? "Saving..." : editingFaq ? "Update FAQ" : "Add FAQ"}
          </button>
          {editingFaq && (
            <button
              type="button"
              onClick={resetForm}
              className="px-4 py-2 border border-[#D0D5DD] dark:border-[#FFFFFF1A] rounded-lg"
            >
              Cancel Edit
            </button>
          )}
        </div>
      </form>

      <div className="border border-[#D0D5DD] dark:border-[#FFFFFF1A] rounded-lg overflow-hidden">
        <div className="px-4 py-3 bg-gray-100 dark:bg-[#FFFFFF1A] font-semibold">
          FAQs
        </div>
        <div className="max-h-[320px] overflow-y-auto">
          {loading ? (
            <div className="p-6 text-center">Loading FAQs...</div>
          ) : faqs.length === 0 ? (
            <div className="p-6 text-center text-gray-500">No FAQs yet.</div>
          ) : (
            faqs.map((faq) => (
              <div
                key={faq.id}
                className="p-4 border-t border-[#D0D5DD] dark:border-[#FFFFFF1A]"
              >
                <p className="font-semibold mb-1">{faq.question}</p>
                <div
                  className="text-sm text-gray-600 dark:text-gray-300 prose prose-sm max-w-none dark:prose-invert [&_a]:text-blue-600 dark:[&_a]:text-blue-400 [&_a]:no-underline hover:[&_a]:text-blue-700 dark:hover:[&_a]:text-blue-300"
                  dangerouslySetInnerHTML={{ __html: faq.answer }}
                />
                <div className="mt-3 flex items-center gap-3">
                  <button
                    type="button"
                    onClick={() => handleEdit(faq)}
                    className="text-blue-600 text-sm"
                  >
                    Edit
                  </button>
                  <button
                    type="button"
                    onClick={() => handleDelete(faq.id)}
                    disabled={deletingId === faq.id}
                    className="text-red-600 text-sm disabled:opacity-70"
                  >
                    {deletingId === faq.id ? "Deleting..." : "Delete"}
                  </button>
                </div>
              </div>
            ))
          )}
        </div>
      </div>
    </Modal>
  );
};

export default ProviderFaqManagementModal;
